import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import {
  sharedPhotoLoader,
  sharedThumbnailLoader,
  useSharedGallery,
} from '@/resources/share/share.service';
import { PhotoGrid } from '@/dashboard/photo-grid';
import { PhotoLightbox } from '@/dashboard/photo-lightbox';
import { EmptyNote, ErrorNote, SectionHeader } from '@/dashboard/ui';

export function SharePage({ token }: { token: string }) {
  const { t } = useTranslation();
  const [openAt, setOpenAt] = useState<number | null>(null);
  const gallery = useSharedGallery(token);

  const photos = gallery.data?.photos ?? [];

  if (gallery.isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center gap-2 text-sm text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" />
        {t('common.oneMoment')}
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-svh w-full max-w-6xl flex-col gap-5 p-4 md:p-6">
      <img
        src="/assets/logo-nargus.png"
        alt="nargus"
        className="h-6 w-auto self-start mix-blend-multiply dark:mix-blend-screen dark:invert"
      />

      <SectionHeader
        title={gallery.data?.title || t('shares.publicTitle')}
        hint={gallery.data?.organizationName}
      />

      <ErrorNote error={gallery.error} />

      {gallery.isError ? null : photos.length === 0 ? (
        <EmptyNote>{t('shares.publicEmpty')}</EmptyNote>
      ) : (
        <PhotoGrid
          photos={photos}
          loadThumbnail={sharedThumbnailLoader(token)}
          onOpen={(photo) => setOpenAt(photos.indexOf(photo))}
        />
      )}

      {openAt !== null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          index={Math.min(openAt, photos.length - 1)}
          onIndex={setOpenAt}
          onClose={() => setOpenAt(null)}
          load={sharedPhotoLoader(token)}
        />
      )}
    </div>
  );
}
